<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const { variant } = defineProps(["variant"]);
const emit = defineEmits(["click"]);

const { t } = useI18n();
const router = useRouter();

const isSubscriptionOpen = ref(false);
const isSingleDocumentOpen = ref(false);

const toggleSubscription = () => {
  isSubscriptionOpen.value = !isSubscriptionOpen.value;
};

const toggleSingleDocument = () => {
  isSingleDocumentOpen.value = !isSingleDocumentOpen.value;
};

const redirectToTemplates = () => {
  router.push("/new-document");
};
</script>

<template>
  <v-row>
    <v-col cols="12" outlined>
      <v-card class="pa-4 card-style">
        <v-row
          align="center"
          justify="space-between"
          @click="toggleSubscription"
          class="cursor-pointer"
        >
          <v-col cols="8">
            <h3 class="card-title">
              {{ t("billing.subscription") }}
            </h3>
          </v-col>
          <v-col cols="1">
            <v-icon>
              <img
                src="/images/icons/arrow.svg"
                class="arrow-image"
                :class="{ 'rotate-arrow': isSubscriptionOpen }"
                alt="image-arrow"
              />
            </v-icon>
          </v-col>
          <v-col cols="3" class="ml-0 text-right">
            <v-btn variant="flat" class="button-select" @click.stop="emit('click')">{{
              t("common.select")
            }}</v-btn>
          </v-col>
        </v-row>

        <v-expand-transition>
          <v-card v-show="isSubscriptionOpen" class="mx-auto border-0">
            <v-divider thickness="2"></v-divider>

            <v-list dense class="pt-2">
              <v-list-item class="align-start mb-1 list-item">
                <div class="info-text">
                  <h4 class="info-title">{{ t("billing.join_premium_subscription_title") }}</h4>
                </div>
              </v-list-item>

              <v-list-item class="align-start mb-1 list-item">
                <div class="text-content">
                  <div class="list-title-with-icon">
                    <v-icon size="20" color="grey-darken-1" class="title-icon">mdi-check-circle</v-icon>
                    {{ t("billing.join_premium_subscription_feature1") }}
                  </div>
                  <p class="list-subtitle">
                    {{ t("billing.join_premium_subscription_feature1_desc") }}
                  </p>
                </div>
              </v-list-item>

              <v-list-item class="d-flex align-start mb-1 list-item">
                <div class="text-content">
                  <div class="list-title-with-icon">
                    <v-icon size="20" color="grey-darken-1" class="title-icon">mdi-check-circle</v-icon>
                    {{ t("billing.join_premium_subscription_feature2") }}
                  </div>
                  <p class="list-subtitle">
                    {{ t("billing.join_premium_subscription_feature2_desc") }}
                  </p>
                </div>
              </v-list-item>

              <v-list-item class="d-flex align-start list-item">
                <div class="text-content">
                  <div class="list-title-with-icon">
                    <v-icon size="20" color="grey-darken-1" class="title-icon">mdi-check-circle</v-icon>
                    {{ t("billing.join_premium_subscription_feature3") }}
                  </div>
                  <p class="list-subtitle">
                    {{ t("billing.join_premium_subscription_feature3_desc") }}
                  </p>
                </div>
              </v-list-item>
            </v-list>
          </v-card>
        </v-expand-transition>
      </v-card>
    </v-col>

    <v-col cols="12" outlined>
      <v-card class="pa-4 card-style">
        <v-row
          align="center"
          justify="space-between"
          @click="toggleSingleDocument"
          class="cursor-pointer"
        >
          <v-col cols="8">
            <h3 class="card-title">
              {{ t("billing.single_document") }}
            </h3>
          </v-col>
          <v-col cols="1">
            <v-icon>
              <img
                src="/images/icons/arrow.svg"
                class="arrow-image"
                :class="{ 'rotate-arrow': isSingleDocumentOpen }"
                alt="image-arrow"
              />
            </v-icon>
          </v-col>
          <v-col cols="3" class="text-right">
            <v-btn variant="flat" class="button-select" @click.stop="redirectToTemplates">{{
              t("common.findDocument")
            }}</v-btn>
          </v-col>
        </v-row>

        <v-expand-transition>
          <v-card v-show="isSingleDocumentOpen" class="mx-auto border-0">
            <v-divider thickness="2"></v-divider>

            <v-list dense class="pt-2">
              <v-list-item class="align-start mb-1 list-item">
                <div class="info-text">
                  <h4 class="info-title">{{ t("billing.single_document_title") }}</h4>
                </div>
              </v-list-item>

              <v-list-item class="align-start mb-1 list-item">
                <div class="text-content">
                  <div class="list-title-with-icon">
                    <v-icon size="20" color="grey-darken-1" class="title-icon">mdi-check-circle</v-icon>
                    {{ t("billing.single_document_feature1") }}
                  </div>
                  <p class="list-subtitle">
                    {{ t("billing.single_document_feature1_desc") }}
                  </p>
                </div>
              </v-list-item>

              <v-list-item class="d-flex align-start mb-1 list-item">
                <div class="text-content">
                  <div class="list-title-with-icon">
                    <v-icon size="20" color="grey-darken-1" class="title-icon">mdi-check-circle</v-icon>
                    {{ t("billing.single_document_feature2") }}
                  </div>
                  <p class="list-subtitle">
                    {{ t("billing.single_document_feature2_desc") }}
                  </p>
                </div>
              </v-list-item>

              <v-list-item class="d-flex align-start mb-1 list-item">
                <div class="text-content">
                  <div class="list-title-with-icon">
                    <v-icon size="20" color="grey-darken-1" class="title-icon">mdi-check-circle</v-icon>
                    {{ t("billing.single_document_feature3") }}
                  </div>
                  <p class="list-subtitle">
                    {{ t("billing.single_document_feature3_desc") }}
                  </p>
                </div>
              </v-list-item>

              <v-list-item class="d-flex align-start list-item">
                <div class="text-content">
                  <div class="list-title-with-icon">
                    <v-icon size="20" color="grey-darken-1" class="title-icon">mdi-check-circle</v-icon>
                    {{ t("billing.single_document_feature4") }}
                  </div>
                  <p class="list-subtitle">
                    {{ t("billing.single_document_feature4_desc") }}
                  </p>
                </div>
              </v-list-item>
            </v-list>
          </v-card>
        </v-expand-transition>
      </v-card>
    </v-col>
  </v-row>
</template>

<style scoped lang="scss">
.card-style {
  border: 2px solid #e0e0e0;
  padding: 0 !important;
  box-shadow: none;
}

.card-title {
  font-weight: bold;
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.125rem;
  color: var(--blue-bonnet);
  margin-left: 15px;
  width: 100%;
}

.button-select {
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  border-radius: 0;
  width: 100px;
  color: black;
  background-color: var(--booger-buster);
}

.rotate-arrow {
  transform: rotate(180deg);
}

.v-divider {
  margin: 0 !important;
}

.v-list-item {
  display: flex;
  align-items: flex-start;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.v-list-item .v-icon {
  margin-top: 0 !important;
  align-self: flex-start;
}

.v-icon {
  float: left;
}

.icon-style {
  margin-right: 15px;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.text-content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
}

.info-text {
  display: flex;
  font-weight: normal;
  line-height: 1.3;
  font-size: 0.875rem;
  color: var(--eerie-black);
  width: 100%;
}

.info-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--eerie-black);
  margin: 0;
}

.list-title-with-icon {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--eerie-black);
  margin-bottom: 3px;
  line-height: 1.3;
}

.title-icon {
  margin-right: 8px !important;
  flex-shrink: 0;
}

.list-subtitle {
  margin: 0;
  color: var(--eerie-black);
  font-size: 0.8rem;
  line-height: 1.3;
  margin-bottom: 6px;
}

.arrow-image {
  margin-left: auto !important;
}
</style>
